import { getRetryDelay } from "api/utils";
import axios from "axios";
import axiosRetry from "axios-retry";
import NotificationService from "components/Notification/Notification.service";
import { actions as authActions } from "containers/Auth/Auth.reducer";
import {
    changeEmailPath,
    changePasswordPath,
    deleteUserPath,
    disableTwoFaPath,
    enableTwoFaPath,
    inviteUserPath,
    loginCheckEventKey,
    loginCheckUrl,
    loginUrl,
    logoutPath,
    resendEmailPath,
    webhookSecurityPath,
} from "containers/Auth/constants";
import { store } from "store";

/**
 * Preparing rest client for react app
 * Here should be all default params defined
 */
const getInitialClient = (axiosInstance) => {
    const typedWindow = window as any;
    const client = axiosInstance.create({
        baseURL: `${typedWindow.REACT_APP_REST_API || ""}${
            typedWindow.REACT_APP_REST_API_NAMESPACE || "/api/latest"
        }`,
        timeout: typedWindow.REACT_APP_API_TIMEOUT,
        paramsSerializer: (params) => {
            // Custom paramsSerializer to replicate axios 0.22.0 behavior:
            // - Spaces encoded as '+' instead of '%20'
            // - Objects JSON stringified with minimal encoding (only quotes and braces)
            // - Arrays use bracket notation: arrayParam[]=1&arrayParam[]=2
            if (!params) return "";

            const parts: string[] = [];

            Object.entries(params).forEach(([key, value]) => {
                if (value === null || value === undefined) return;

                if (Array.isArray(value)) {
                    // Arrays: arrayParam[]=1&arrayParam[]=2
                    value.forEach((item) => {
                        parts.push(
                            `${encodeURIComponent(key)}[]=${encodeURIComponent(String(item))}`
                        );
                    });
                } else if (typeof value === "object") {
                    if (key === "variables") {
                        // Special variables handling: pre-encode + in order values
                        const processedValue: any = { ...value };
                        if (processedValue.order && typeof processedValue.order === "string") {
                            // Replace + with a placeholder, then encode, then restore as %2B
                            processedValue.order = processedValue.order.replace(/\+/g, "__PLUS__");
                        }

                        let encoded = encodeURIComponent(JSON.stringify(processedValue));

                        // Restore + as properly encoded %2B
                        encoded = encoded.replace(/__PLUS__/g, "%2B");

                        parts.push(`${encodeURIComponent(key)}=${encoded}`);
                    } else {
                        parts.push(
                            `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`
                        );
                    }
                } else {
                    // Primitives: key=value
                    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
                }
            });

            // Convert spaces to + (axios 0.22.0 behavior)
            return parts.join("&").replace(/%20/g, "+");
        },
    });

    client.defaults.withCredentials = true;

    return client;
};

const handleInterceptors = (client) => {
    // handle generic events - like loading and 500 type errors - in API interceptors
    client.interceptors.request.use(
        (config) =>
            // display a single subtle loader on the top of the page when there is networking in progress
            // avoid multiple loaders, use placeholders or consistent updates instead
            config
    );

    client.interceptors.response.use(
        (resp) => {
            if (resp?.request?.responseURL?.includes(loginCheckUrl)) {
                window.dispatchEvent(
                    new CustomEvent(loginCheckEventKey, {
                        detail: { data: resp?.data, success: true },
                    })
                );
            }
            return resp;
        },
        (err) => {
            if (axios.isCancel(err)) {
                return Promise.reject(err);
            }
            const { status } = err.response || {};

            if (
                err?.request?.responseURL?.includes(loginCheckUrl) ||
                err?.response?.request?.responseURL?.includes(loginCheckUrl) ||
                err?.config?.url?.includes(loginCheckUrl)
            ) {
                window.dispatchEvent(
                    new CustomEvent(loginCheckEventKey, {
                        detail: {
                            error: err?.response?.data,
                            success: false,
                            status: err?.response?.status,
                        },
                    })
                );
            }

            const responseUrl = err?.response?.config?.url;
            const canLogout =
                responseUrl !== loginCheckUrl &&
                responseUrl !== loginUrl &&
                responseUrl !== logoutPath &&
                responseUrl !== changePasswordPath &&
                responseUrl !== changeEmailPath &&
                responseUrl !== enableTwoFaPath &&
                responseUrl !== disableTwoFaPath &&
                responseUrl !== resendEmailPath &&
                responseUrl !== deleteUserPath &&
                responseUrl !== inviteUserPath &&
                responseUrl !== webhookSecurityPath;

            if (status === 401 && canLogout) {
                store.dispatch({ type: authActions.START_SIGN_OUT });
            }
            throw err;
        }
    );
};

const restClient = getInitialClient(axios);
handleInterceptors(restClient);

const retryRestClient = getInitialClient(axios);
handleInterceptors(retryRestClient);

axiosRetry(retryRestClient, {
    retries: 1,
    retryDelay: (_, error) => {
        return getRetryDelay(error) * 1000;
    },
    retryCondition: (error) => {
        return error?.response?.status === 600;
    },
    onRetry: (_, error) => {
        const delayInSeconds = getRetryDelay(error);
        const plural = delayInSeconds > 1 ? "s" : "";
        NotificationService.warn(
            `The repository is currently busy. Your request will be automatically retried in ${delayInSeconds} second${plural}. Please wait.`,
            { autoClose: 5000 }
        );
    },
});

export { retryRestClient };

export default restClient;
